<script setup lang="ts">
import { type Locale, type Money, formatMoney } from '@taler/domain'
import type { ReconciliationProposal } from '../internal/types.ts'
import { Card } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AccountLookup } from '../internal/account-index.ts'

const props = defineProps<{
  proposal: ReconciliationProposal
  accounts: ReadonlyMap<string, AccountLookup>
  applyReallocations: boolean
  matchedCount: number
  notCheckedCount: number
  locale: Locale
}>()

const emit = defineEmits<{ 'update:applyReallocations': [value: boolean] }>()

const { t } = useI18n()

function labelFor(accountId: string): string {
  return props.accounts.get(accountId)?.label ?? accountId
}

function moneyText(value: Money, sign?: boolean): string {
  return formatMoney(value, {
    locale: props.locale,
    ...(sign === undefined ? {} : { sign }),
  })
}

const hasAnything = computed(
  () => props.proposal.reallocations.length > 0 || props.proposal.residuals.length > 0,
)
</script>

<template>
  <Card padding="block" class="flex flex-col gap-[var(--space-inline)]">
    <h2
      class="text-[length:var(--text-step-2)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
    >
      {{ t('reconciliation.editor.proposalTitle') }}
    </h2>

    <div v-if="proposal.reallocations.length > 0" class="flex flex-col gap-[var(--space-tight)]">
      <p v-for="pair in proposal.reallocations" :key="`${pair.fromAccountId}-${pair.toAccountId}`">
        <span aria-hidden="true">⇄</span>
        {{ labelFor(pair.fromAccountId) }} → {{ labelFor(pair.toAccountId) }}
        <span class="tabular-nums font-medium">{{ moneyText(pair.amount) }}</span>
        <span class="text-[color:var(--color-text-muted)]">
          — {{ t('reconciliation.editor.transferHint') }}</span
        >
      </p>
      <label class="flex items-center gap-[var(--space-inline)]">
        <input
          type="checkbox"
          :checked="applyReallocations"
          @change="emit('update:applyReallocations', ($event.target as HTMLInputElement).checked)"
        />
        {{ t('reconciliation.editor.applyReallocations') }}
      </label>
    </div>

    <p v-for="residual in proposal.residuals" :key="residual.accountId">
      <span aria-hidden="true">±</span>
      {{ labelFor(residual.accountId) }}
      <span class="tabular-nums font-medium">{{ moneyText(residual.amount, true) }}</span>
      →
      {{
        residual.target === 'PNL'
          ? t('reconciliation.editor.gainLoss')
          : t('reconciliation.editor.correction')
      }}
    </p>

    <p v-if="!hasAnything" class="text-[color:var(--color-text-muted)]">
      {{ t('reconciliation.editor.nothingToPost') }}
    </p>

    <p class="text-[color:var(--color-text-muted)]">
      {{ t('reconciliation.editor.summaryLine', { matched: matchedCount, notChecked: notCheckedCount }) }}
    </p>
  </Card>
</template>
